import React, { useState, useMemo, useEffect } from 'react';
import { Text, Box, Heading, Select as ChakraSelect, VStack, HStack, Button, useToast, Collapse, IconButton } from '@chakra-ui/react';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import { useContext } from 'react';
import { CHALLENGE_VIRTUAL_STUDY_GROUP_ID } from './constants';
import { useIsMobile } from '../../../../../hooks/isMobile';
import useInteractiveQuizAnswersFromActiveLicenses from '../../../hooks/useInteractiveQuizAnswersFromActiveLicenses';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import axios from 'axios';
import { NumberInput, NumberInputField, NumberInputStepper, NumberIncrementStepper, NumberDecrementStepper } from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { StemiBeatLoader } from '../../../../../components/Loader';
import { currentSchoolYear } from '../../../../../helpers/schoolYear';

interface QuizAnswer {
    answer_id: string;
    answers: string;
    school_name: string | null;
    school_id: string | null;
    student_name: string | null;
    student_username: string | null;
    student_id: string;
    study_group_id: string;
    unique_quiz_id: string;
    program_id: string | null;
    program_name: string | null;
    is_correct: boolean;
    points: number;
    max_points: number;
    created_at: string;
    manually_graded: boolean;
    actual_student_school_name: string | null;
}

interface AdminInteractiveQuizAnswersProps {
    unique_quiz_id: string;
    question_type: string;
    answers: any;
    max_points: number;
    question_id?: string;
}

const AdminInteractiveQuizAnswers: React.FC<AdminInteractiveQuizAnswersProps> = ({
    unique_quiz_id,
    question_type,
    answers,
    max_points,
    question_id
}) => {
    const user = useContext(AuthContext);
    const [selectedSchoolName, setSelectedSchoolName] = useState<string>('');
    const [selectedSchoolYear, setSelectedSchoolYear] = useState<string>(currentSchoolYear());
    const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
    const [points, setPoints] = useState<number>(0);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    const { quizAnswers, availableSchoolYears, isLoading, error } = useInteractiveQuizAnswersFromActiveLicenses(unique_quiz_id, question_id, selectedSchoolYear);
    const isMobile = useIsMobile();
    const { t } = useTranslation();
    const toast = useToast();

    if (!user?.roles.includes(Role.staff)) {
        return null;
    }

    const schools = useMemo(() => {
        const names = new Set<string>();
        quizAnswers.forEach((answer: QuizAnswer) => {
            if (answer.study_group_id === CHALLENGE_VIRTUAL_STUDY_GROUP_ID) {
                if (answer.actual_student_school_name) {
                    names.add(answer.actual_student_school_name);
                }
            } else if (answer.school_name) {
                names.add(answer.school_name);
            }
        });
        return Array.from(names).sort((a, b) => a.localeCompare(b));
    }, [quizAnswers]);

    useEffect(() => {
        if (schools.length > 0 && !selectedSchoolName) {
            setSelectedSchoolName(schools[0]!);
        }
    }, [schools, selectedSchoolName]);

    useEffect(() => {
        if (availableSchoolYears.length > 0 && !availableSchoolYears.includes(selectedSchoolYear)) {
            setSelectedSchoolYear(availableSchoolYears[0]!);
            setSelectedSchoolName('');
            setSelectedStudentId(null);
        }
    }, [availableSchoolYears]);

    const answerMatchesSchool = (answer: QuizAnswer) => {
        if (!selectedSchoolName) return true;
        if (answer.study_group_id === CHALLENGE_VIRTUAL_STUDY_GROUP_ID) {
            return answer.actual_student_school_name === selectedSchoolName;
        }
        return answer.school_name === selectedSchoolName;
    };

    const studentOptions = useMemo(() => {
        const studentMap = new Map<string, { value: string; label: string }>();
        quizAnswers.filter(answerMatchesSchool).forEach((answer: QuizAnswer) => {
            studentMap.set(answer.student_id, {
                value: answer.student_id,
                label: answer.student_name || answer.student_username || answer.student_id
            });
        });
        return Array.from(studentMap.values()).sort((a, b) =>
            a.label.localeCompare(b.label)
        );
    }, [quizAnswers, selectedSchoolName]);

    const filteredAnswers = useMemo(() => {
        let filtered = quizAnswers.filter(answerMatchesSchool);
        if (selectedStudentId) {
            filtered = filtered.filter((answer: QuizAnswer) =>
                answer.student_id === selectedStudentId
            );
        }
        return filtered;
    }, [quizAnswers, selectedSchoolName, selectedStudentId]);

    const handleGradeAnswer = async (answer: QuizAnswer) => {
        try {
            await axios.patch(`/api/v1/syllabus/interactive_quiz_answers/`, {
                study_group_id: answer.study_group_id,
                student_id: answer.student_id,
                question_id: answer.unique_quiz_id,
                answer_id: answer.answer_id,
                points: points
            }, {
                headers: {
                    'X-CSRFToken': JSON.parse(
                        document.getElementById('csrf_token')!.innerHTML,
                    ),
                },
            });

            toast({
                title: t('plugin.interactiveQuiz.gradeSubmitted'),
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: t('plugin.interactiveQuiz.gradeError'),
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
    };

    if (error) {
        return <Text color="red.500">{t('plugin.interactiveQuiz.errorLoadingAnswers')}: {error}</Text>;
    }

    return (
        <Box mt={8} p={4} borderWidth="1px" borderRadius="lg" width="100%">
            <HStack justify="space-between" mb={4}>
                <Heading size="md">{t('plugin.interactiveQuiz.adminViewTitle')}</Heading>
                <IconButton
                    aria-label={isOpen ? t('plugin.interactiveQuiz.collapse') : t('plugin.interactiveQuiz.expand')}
                    icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={() => setIsOpen(!isOpen)}
                    variant="ghost"
                />
            </HStack>

            <Collapse in={isOpen}>
                <VStack spacing={4} align="stretch" p="1rem">
                    <Box display="flex" gap={4} flexWrap="wrap">
                        {availableSchoolYears.length > 1 && (
                            <Box flex="1" minW="200px">
                                <Text mb={2}>School Year:</Text>
                                <ChakraSelect
                                    value={selectedSchoolYear}
                                    onChange={(e) => {
                                        setSelectedSchoolYear(e.target.value);
                                        setSelectedSchoolName('');
                                        setSelectedStudentId(null);
                                    }}
                                >
                                    {availableSchoolYears.map((year) => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </ChakraSelect>
                            </Box>
                        )}
                        <Box flex="1" minW="200px">
                            <Text mb={2}>{t('filter.school')}:</Text>
                            <ChakraSelect
                                value={selectedSchoolName}
                                onChange={(e) => {
                                    setSelectedSchoolName(e.target.value);
                                    setSelectedStudentId(null);
                                }}
                            >
                                {schools.map((name) => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </ChakraSelect>
                        </Box>
                    </Box>

                    <Box>
                        <Text mb={2}>{t('plugin.interactiveQuiz.selectStudent')}:</Text>
                        <Select
                            styles={{
                                container: (provided: any) => ({
                                    ...provided,
                                    minWidth: '350px',
                                    textAlign: 'left',
                                }),
                            }}
                            isClearable
                            placeholder={t('plugin.interactiveQuiz.selectStudent')}
                            noOptionsMessage={() => t('plugin.interactiveQuiz.noStudents')}
                            options={studentOptions}
                            value={studentOptions.find((o) => o.value === selectedStudentId) ?? null}
                            onChange={(value: { value: string; label: string } | null) => {
                                setSelectedStudentId(value?.value || null);
                            }}
                            menuPlacement="auto"
                            menuPortalTarget={document.body}
                        />
                    </Box>

                    <Box
                        maxH="700px"
                        overflowY="auto"
                        css={{
                            '&::-webkit-scrollbar': {
                                width: '4px',
                            },
                            '&::-webkit-scrollbar-track': {
                                width: '6px',
                                background: 'gray.100',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'gray.400',
                                borderRadius: '24px',
                            },
                        }}
                    >
                        {isLoading ? (
                            <StemiBeatLoader />
                        ) : filteredAnswers.length === 0 ? (
                            <Text>{t('plugin.interactiveQuiz.noAnswersFound')}{selectedSchoolName ? t('plugin.interactiveQuiz.noAnswersFoundForSchool') : ''}</Text>
                        ) : (
                            filteredAnswers.map((answer: QuizAnswer, index: number) => (
                                <Box
                                    key={`${answer.answer_id}-${index}`}
                                    p={4}
                                    mb={4}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    bg="gray.50"
                                >
                                    <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', margin: '4px', justifyContent: isMobile ? 'flex-start' : 'space-between', width: '100%' }}>
                                        <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            <Text fontWeight="medium">{answer.student_name}</Text>
                                            {!answer.student_name && <Text fontSize="sm">{t('general.username')}: {answer.student_username}</Text>}
                                            {answer.program_name && (
                                                <Text fontSize="sm">{t('filter.program')}: {answer.program_name}</Text>
                                            )}
                                            {answer.actual_student_school_name && (
                                                <Text fontSize="sm">{t('filter.school')}: {answer.actual_student_school_name}</Text>
                                            )}
                                            <Text fontSize="sm">{t('plugin.assestment.points')}: {answer.points}/{max_points}</Text>
                                            <Text fontSize="sm">{t('analytics.legend.correct')}: {answer.is_correct ? t('survey.yes') : t('survey.no')}</Text>
                                            <Text fontSize="sm">{t('plugin.interactiveQuiz.manuallyGraded')}: {answer.manually_graded ? t('survey.yes') : t('survey.no')}</Text>
                                            <Box p={4} borderWidth="1px" borderRadius="md" bg="white" w="full">
                                                {question_type === 'ABCDQuestion' ? (
                                                    <Text w="full" whiteSpace="pre-wrap" wordBreak="break-word">
                                                        {answer.answers.split(',').map((key: string) => {
                                                            const nkey = Number(key);
                                                            return answers[nkey]?.answer || '';
                                                        }).join(', ')}
                                                    </Text>
                                                ) : (
                                                    <Text w="full" whiteSpace="pre-wrap" wordBreak="break-word">{answer.answers}</Text>
                                                )}
                                            </Box>
                                        </span>
                                    </div>
                                    {!answer.manually_graded && (
                                        <HStack mt={4} spacing={4}>
                                            <NumberInput
                                                size="sm"
                                                maxW={24}
                                                min={0}
                                                max={max_points}
                                                value={points}
                                                onChange={(_, value) => setPoints(value)}
                                            >
                                                <NumberInputField />
                                                <NumberInputStepper>
                                                    <NumberIncrementStepper />
                                                    <NumberDecrementStepper />
                                                </NumberInputStepper>
                                            </NumberInput>
                                            <Button
                                                size="sm"
                                                colorScheme="blue"
                                                onClick={() => handleGradeAnswer(answer)}
                                            >
                                                {t('plugin.interactiveQuiz.gradeAnswer')}
                                            </Button>
                                        </HStack>
                                    )}
                                </Box>
                            ))
                        )}
                    </Box>
                </VStack>
            </Collapse>
        </Box>
    );
};

export default AdminInteractiveQuizAnswers;
